import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const pairs = (s, a, b) => lines(s).map((l) => { const [x, ...y] = l.split("::"); return { [a]: x.trim(), [b]: y.join("::").trim() }; });

const toForm = (p) => ({
  title: p?.title || "", slug: p?.slug || "", client: p?.client || "", category: p?.category || "",
  tagline: p?.tagline || "", summary: p?.summary || "", overview: p?.overview || "", objective: p?.objective || "", role: p?.role || "",
  features: (p?.features || []).map((f) => `${f.title} :: ${f.text}`).join("\n"),
  stats: (p?.stats || []).map((s) => `${s.value} :: ${s.label}`).join("\n"),
  technologies: (p?.technologies || []).join(", "), results: (p?.results || []).join("\n"),
  cover_image: p?.cover_image || "", gallery: (p?.gallery || []).join("\n"),
  live_url: p?.live_url || "", source_url: p?.source_url || "", accent: p?.accent || "#0055FF",
  size: p?.size || "medium", order: p?.order ?? 100, published: p?.published ?? true,
});

export const fromForm = (f) => ({
  ...f, order: Number(f.order) || 0,
  features: pairs(f.features, "title", "text"), stats: pairs(f.stats, "value", "label"),
  technologies: f.technologies.split(",").map((x) => x.trim()).filter(Boolean),
  results: lines(f.results), gallery: lines(f.gallery),
});

const inp = "mt-1.5 w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0055FF]";

const F = ({ label, hint, children }) => (
  <label className="block text-sm font-bold text-slate-700">{label}{hint && <span className="ml-1 font-normal text-slate-500">({hint})</span>}{children}</label>
);

export const ProjectForm = ({ open, project, categories, onClose, onSave }) => {
  const [f, setF] = useState(() => toForm(project));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const T = (k, testid) => ({ value: f[k], onChange: set(k), className: inp, "data-testid": testid });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSave(fromForm(f)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="project-form-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto rounded-3xl">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">{project ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <F label="Title *"><input required {...T("title", "pf-title")} /></F>
          <F label="Category *"><input required list="cat-list" {...T("category", "pf-category")} /><datalist id="cat-list">{categories.map((c) => <option key={c} value={c} />)}</datalist></F>
          <F label="URL slug" hint="auto from title"><input {...T("slug", "pf-slug")} /></F>
          <F label="Client"><input {...T("client", "pf-client")} /></F>
          <div className="sm:col-span-2"><F label="Tagline"><input {...T("tagline", "pf-tagline")} /></F></div>
          <div className="sm:col-span-2"><F label="Short description" hint="shown on cards"><textarea rows={2} {...T("summary", "pf-summary")} /></F></div>
          <div className="sm:col-span-2"><F label="Overview"><textarea rows={3} {...T("overview", "pf-overview")} /></F></div>
          <F label="Problem / objective"><textarea rows={2} {...T("objective", "pf-objective")} /></F>
          <F label="Our role / contribution"><textarea rows={2} {...T("role", "pf-role")} /></F>
          <div className="sm:col-span-2"><F label="Features" hint="one per line: Title :: description"><textarea rows={4} {...T("features", "pf-features")} /></F></div>
          <F label="Results" hint="one per line"><textarea rows={3} {...T("results", "pf-results")} /></F>
          <F label="Stats" hint="one per line: 300+ :: Happy Customers"><textarea rows={3} {...T("stats", "pf-stats")} /></F>
          <F label="Technologies / tools" hint="comma separated"><input {...T("technologies", "pf-tech")} /></F>
          <F label="Cover image URL" hint="/projects/x.webp or https://"><input {...T("cover_image", "pf-cover")} /></F>
          <F label="Live project URL"><input type="url" {...T("live_url", "pf-live")} /></F>
          <F label="Source code URL"><input type="url" {...T("source_url", "pf-source")} /></F>
          <F label="Accent colour"><input type="color" {...T("accent", "pf-accent")} className="mt-1.5 h-11 w-full rounded-xl border-2 border-slate-200" /></F>
          <F label="Card size">
            <select {...T("size", "pf-size")}><option value="large">Large</option><option value="medium">Medium</option><option value="tall">Tall</option><option value="wide">Wide</option></select>
          </F>
          <F label="Display order"><input type="number" {...T("order", "pf-order")} /></F>
          <label className="flex items-center gap-3 self-end pb-3 text-sm font-bold text-slate-700"><input data-testid="pf-published" type="checkbox" checked={f.published} onChange={set("published")} className="h-5 w-5 accent-[#0055FF]" /> Published</label>
          <div className="flex justify-end gap-3 sm:col-span-2">
            <button type="button" data-testid="pf-cancel" onClick={onClose} className="rounded-full px-6 py-3 font-bold text-slate-700 hover:bg-slate-100">Cancel</button>
            <button data-testid="pf-save" disabled={busy} className="flex items-center gap-2 rounded-full bg-[#0055FF] px-6 py-3 font-bold text-white hover:bg-[#0043cc] disabled:opacity-70">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save project</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
